import { Box, Button, Dialog, IconButton, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import { useCallback, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  SkuContentRule,
  Skus,
  AdminListRequestOfBaseFilter,
  ISkuContentRuleSku,
  ListResponseOfSkuContentRuleSku,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import useBoolean from "hooks/useBoolean";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import SkuRuleDetailSkuContentForm from "./SkuRuleDetailSkuContentForm";

type SkuRuleDetailSkuContentsListProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  dialogTitleKey: string;
};

const SkuRuleDetailSkuContentsList = ({
  sku,
  skuContentRule,
  readonly,
  dialogTitleKey,
  ...props
}: SkuRuleDetailSkuContentsListProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<ISkuContentRuleSku | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    skuContentRuleSkuToEdit,
    setSkuContentRuleSkuToEdit,
  ] = useState<ISkuContentRuleSku | null>(null);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sku.sku");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      await skuService.deleteSkuContentRuleSku(sku.id!, skuContentRule.id, id);
      queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "skuRuleSkuContentFilterDeleted" }))
      );
    }
  }, [deleting?.id, skuService, sku.id, skuContentRule.id, queryClient, t]);

  const response = useQuery<ListResponseOfSkuContentRuleSku>(
    [
      "skuRuleDetails",
      sku.id,
      skuContentRule.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      skuService.getSkuContentRuleSkus(
        sku.id!,
        skuContentRule.id,
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      )
  );

  interface SkuContentRuleSkuWithId extends ISkuContentRuleSku {
    id: string;
  }

  const skuContentRuleSkus = (response?.data?.data ??
    []) as SkuContentRuleSkuWithId[];

  if (response.isLoading) return <Loader />;
  if (response.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onSkuRuleEditClose = () => {
    createOpenActions.setFalse();
    setSkuContentRuleSkuToEdit(null);
  };

  const onSkuRuleCreated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const onSkuRuleUpdated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const DeleteButton = ({ value }: { value: ISkuContentRuleSku }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => setDeleting(value)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: ISkuContentRuleSku }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => {
            setSkuContentRuleSkuToEdit(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      customSortAccessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuContentsTitle" }),
    },
    {
      accessor: "title",
      customSortAccessor: "title",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuTitleColumnLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  return (
    <>
      {!readonly && (
        <Box textAlign="right">
          <Button onClick={createOpenActions.setTrue} startIcon={<AddIcon />}>
            <T id="addFilterButton" />
          </Button>
        </Box>
      )}

      <LazyTable
        {...{
          rows: skuContentRuleSkus,
          loading: response.isFetching,
          dense: true,
          cells,
          rowCount: response?.data?.totalRecords ?? 0,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey="skuRuleSkuContentFilterDeleteTitle"
        contentKey="skuRuleFilterDeleteQuestion"
        open={!!deleting}
        handleDelete={handleDelete}
        onCancel={() => setDeleting(null)}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuRuleDetailSkuContentForm
          onClose={onSkuRuleEditClose}
          onCreate={onSkuRuleCreated}
          onUpdate={onSkuRuleUpdated}
          parentSkuId={skuContentRule.skuId}
          skuRuleId={skuContentRule.id}
          skuContentRuleSku={skuContentRuleSkuToEdit}
          dialogTitleKey={dialogTitleKey}
          {...props}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default SkuRuleDetailSkuContentsList;
